import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import {
    AdornedInput, Adornment, Flex, FormField, Label, Paragraph, Text,
} from '@zillow/constellation';
import {
    Center, CurrencyInput, Col, Container, Row,
} from '@zillow/zrs-comet';

import RentGuaranteeActions from 'app/shared/actions/RentGuaranteeActions';

import WizardBodyTemplate from 'app/shared/components/WizardBodyTemplate';
import WizardActionFooter from 'app/shared/components/WizardActionFooter';

import RadioButton from '../components/RadioButton';

const ADD_RENT_GUARANTEE_FEE = 'addRentGuaranteeFee';
const SUBTRACT_RENT_GUARANTEE_FEE = 'subtractRentGuaranteeFee';
const RENT_GUARANTEE_FEE = 30;

const propTypes = {
    onGoToPrevStep: PropTypes.func.isRequired,
    onGoToNextStep: PropTypes.func.isRequired,
};
const defaultProps = {};

const PriceYourRental = ({
    onGoToNextStep,
    onGoToPrevStep,
}) => {
    const dispatch = useDispatch();
    const monthlyRentSelector = useSelector(state => state.rentGuarantee.monthlyRent);
    const [monthlyRent, setMonthlyRent] = useState(monthlyRentSelector);
    const [paymentMethod, setPaymentMethod] = useState(null);
    const rentGuaranteeFeeAdded = Number(monthlyRent) + RENT_GUARANTEE_FEE;
    const rentGuaranteeFeeSubtracted = Number(monthlyRent) + RENT_GUARANTEE_FEE;

    const onMonthlyRentChange = (event) => {
        const value = Number(event.target.value);
        setMonthlyRent(value);
    };

    const handleOnNextClick = () => {
        dispatch(RentGuaranteeActions.setMonthlyRent(monthlyRent));
        onGoToNextStep();
    };

    return (
        <WizardBodyTemplate title="Two ways to pay" subtitle="First, set the monthly rent for the property. ">
            <FormField
                marginY="xl"
                label={<Label>Monthly rent</Label>}
                control={(
                    <AdornedInput
                        leftAdornment={<Adornment>$</Adornment>}
                        input={(
                            <CurrencyInput
                                value={monthlyRent}
                                onChange={onMonthlyRentChange}
                            />
                        )}
                    />
                )}
            />
            <Flex display="flex" justifyContent="center">
                <Container>
                    <Row>
                        <Center>
                            <Text marginBottom="sm">
                                {`Next, choose how to pay the $${RENT_GUARANTEE_FEE} monthly fee for Rent Guarantee. The
                                payment won’t be due until the tenant makes their first rent payment.`}
                            </Text>
                        </Center>
                    </Row>
                    <Row>
                        <Col xs={6}>
                            <RadioButton
                                onClick={() => setPaymentMethod(ADD_RENT_GUARANTEE_FEE)}
                                name="payment-method"
                                id="add-to-monthly-rent"
                                label={`Monthly rent + $${RENT_GUARANTEE_FEE}`}
                                description={(
                                    <>
                                        <Paragraph fontType="bodySmall">
                                            Tenant pays $
                                            {rentGuaranteeFeeAdded}
                                        </Paragraph>
                                        <Paragraph fontType="bodySmall">
                                            You receive $
                                            {monthlyRent}
                                        </Paragraph>
                                    </>
                                )}
                                checked={paymentMethod === ADD_RENT_GUARANTEE_FEE}
                                helperText={(
                                    <div>
                                        <Text fontType="bodyHeading">Add </Text>
                                        <Text>it to the monthly rent tenants pays</Text>
                                    </div>
                                )}
                            />
                        </Col>
                        <Col xs={6}>
                            <RadioButton
                                onClick={() => setPaymentMethod(SUBTRACT_RENT_GUARANTEE_FEE)}
                                name="payment-method"
                                id="subtract-to-monthly-rent"
                                label={`Monthly rent - $${RENT_GUARANTEE_FEE}`}
                                description={(
                                    <>
                                        <Paragraph fontType="bodySmall">
                                            Tenant pays $
                                            {monthlyRent}
                                        </Paragraph>
                                        <Paragraph fontType="bodySmall">
                                            You receive $
                                            {rentGuaranteeFeeSubtracted}
                                        </Paragraph>
                                    </>
                                )}
                                checked={paymentMethod === SUBTRACT_RENT_GUARANTEE_FEE}
                                helperText={(
                                    <div>
                                        <Text fontType="bodyHeading">Subtract </Text>
                                        <Text>it from the monthly rent you receive</Text>
                                    </div>
                                )}
                            />
                        </Col>
                    </Row>
                </Container>
            </Flex>
            <WizardActionFooter
                onBackClick={onGoToPrevStep}
                onNextClick={handleOnNextClick}
                isNextDisabled={!paymentMethod || !monthlyRent}
            />
        </WizardBodyTemplate>
    );
};

PriceYourRental.propTypes = propTypes;
PriceYourRental.defaultProps = defaultProps;

export default PriceYourRental;
